import {
  buildOptimizerCampaignOptions,
  extractImportedReportingCampaigns,
  IMPORTED_REPORTING_CONTEXT_PATH,
  resolveAutoCampaignOptionLabel,
  resolveCanonicalCampaigns,
  resolveDefaultCampaignAnalysisCampaign,
} from "./optimusCampaignOptions";

export {
  extractImportedReportingCampaigns,
  IMPORTED_REPORTING_CONTEXT_PATH,
  resolveAutoCampaignOptionLabel,
  resolveCanonicalCampaigns,
  resolveDefaultCampaignAnalysisCampaign,
};

export const buildCampaignAnalysisCampaignOptions = buildOptimizerCampaignOptions;

const toFiniteNumber = (value) => {
  if (value == null || value === "") return 0;
  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[$,%\s,]/g, ""));
  return Number.isFinite(numeric) ? numeric : 0;
};

export const campaignAnalysisOptionHasUsableMetrics = (option = {}) => {
  const metrics = option.metrics || option.kpis || option.currentKpis || {};
  return [
    option.spend,
    option.totalSpend,
    option.spendToDate,
    option.impressions,
    option.clicks,
    metrics.spend,
    metrics.totalSpend,
    metrics.spendToDate,
    metrics.impressions,
    metrics.clicks,
  ].some((value) => toFiniteNumber(value) > 0);
};

const campaignAnalysisOptionHasLiveUsableMetrics = (option = {}) =>
  campaignAnalysisOptionHasUsableMetrics(option?.liveMetrics || {});

export const campaignAnalysisUsesReportingFallback = (option = null) =>
  Boolean(
    option?.reportingOnly ||
      (option?.hasReportingContext && !campaignAnalysisOptionHasLiveUsableMetrics(option))
  );

/*
 * The AD-PLATFORM campaign id to filter provider reads by — Meta insights,
 * StackAdapt live delivery — or null when the selected campaign has no platform
 * identity at all.
 *
 * The `id` fallback is only sound for an option that CAME from a platform: a
 * live row's id IS its provider campaign id. A governed campaign option's id is
 * its media campaign id, and handing that to a provider asks for a campaign
 * that does not exist there — which either errors or, worse, silently returns
 * the advertiser's unfiltered delivery. A governed campaign the platforms never
 * minted an id for has no live campaign id, and null is the truthful answer.
 *
 * Null means "not addressable by this provider", never "widen the query". See
 * campaignAnalysisProviderScope for what callers must do with it.
 */
export const resolveCampaignAnalysisLiveCampaignId = ({
  selectedCampaignId,
  selectedCampaign,
} = {}) => {
  if (!selectedCampaignId || selectedCampaignId === "auto") return null;
  if (selectedCampaign?.liveCampaignId) return selectedCampaign.liveCampaignId;
  if (selectedCampaign?.externalCampaignId) return selectedCampaign.externalCampaignId;
  if (selectedCampaign?.reportingOnly || selectedCampaign?.governed) return null;
  return selectedCampaign?.id || null;
};

export const resolveCampaignAnalysisContextCampaignId = ({
  selectedCampaignId,
  selectedCampaign,
} = {}) => {
  if (!selectedCampaignId || selectedCampaignId === "auto") return "auto";
  return (
    selectedCampaign?.liveCampaignId ||
    selectedCampaign?.externalCampaignId ||
    selectedCampaign?.reportingCampaignId ||
    selectedCampaign?.mediaCampaignId ||
    selectedCampaign?.id ||
    selectedCampaignId
  );
};

export const resolveCampaignAnalysisReportingCampaignId = ({
  selectedCampaignId,
  selectedCampaign,
} = {}) => {
  if (!selectedCampaignId || selectedCampaignId === "auto") return null;
  return selectedCampaign?.reportingCampaignId || selectedCampaign?.mediaCampaignId || null;
};
